import { useEffect, useRef, useState } from 'react';

function fmtTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleTimeString('es-MX', { hour12: false });
}

function tempColor(t) {
  if (t == null || isNaN(t)) return '#8b96ad';
  if (t <= -18) return '#60a5fa';   // congelación
  if (t <= 0) return '#38bdf8';     // frío
  if (t <= 8) return '#34d399';     // refrigeración
  if (t <= 25) return '#fbbf24';    // ambiente
  return '#f87171';                 // caliente
}

export default function Monitoreo({ devices, authHeaders, socketRef, initialDevice = '' }) {
  const [device, setDevice] = useState(initialDevice || '');
  const [latest, setLatest] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const chartRef = useRef(null);
  const chartInstRef = useRef(null);

  async function load(dev) {
    const id = dev !== undefined ? dev : device;
    if (!id) { setLatest(null); setHistory([]); return; }
    setLoading(true);
    try {
      const res = await fetch('/api/sensors?deviceId=' + encodeURIComponent(id) + '&limit=500', { headers: authHeaders() });
      const d = await res.json();
      setLatest(d.latest || null);
      setHistory(Array.isArray(d.history) ? d.history : []);
    } catch (e) {
      console.error('sensors', e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (initialDevice) { setDevice(initialDevice); load(initialDevice); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDevice]);

  useEffect(() => {
    const socket = socketRef && socketRef.current;
    if (!socket) return;
    const handler = (s) => {
      if (!s || s.deviceId !== device) return;
      const point = { t: s.receivedAt, temps: s.temps || [], errors: s.errors, batt: s.batt, rssi: s.rssi };
      setLatest(point);
      setHistory((prev) => [...prev.slice(-499), point]);
    };
    socket.on('box-sensor', handler);
    return () => { socket.off('box-sensor', handler); };
  }, [device, socketRef]);

  useEffect(() => {
    const canvas = chartRef.current;
    if (!canvas || !history.length) return;
    if (chartInstRef.current) chartInstRef.current.destroy();
    const light = document.documentElement.dataset.theme === 'light';
    const tc = light ? '#1a2233' : '#e6ebf5';
    const gc = light ? '#d9e0ea' : '#263047';
    const labels = history.map((p) => (p.t || '').slice(11, 16));
    const maxSensors = Math.max(1, ...history.map((p) => (p.temps || []).length));
    const datasets = [];
    for (let i = 0; i < maxSensors; i++) {
      datasets.push({
        label: 'Sensor ' + (i + 1) + ' (°C)',
        data: history.map((p) => { const t = (p.temps || [])[i]; return t == null ? null : Number(t); }),
        borderColor: ['#f87171', '#38bdf8', '#34d399', '#fbbf24'][i % 4],
        backgroundColor: 'rgba(0,0,0,0)',
        fill: false,
        tension: 0.3,
        spanGaps: true,
      });
    }
    chartInstRef.current = new Chart(canvas, {
      type: 'line',
      data: { labels, datasets },
      options: {
        responsive: true,
        animation: false,
        plugins: { legend: { labels: { color: tc } } },
        scales: {
          x: { ticks: { color: tc }, grid: { color: gc } },
          y: { ticks: { color: tc, callback: (v) => v + '°' }, grid: { color: gc } },
        },
      },
    });
    return () => { if (chartInstRef.current) { chartInstRef.current.destroy(); chartInstRef.current = null; } };
  }, [history]);

  const temps = latest && Array.isArray(latest.temps) ? latest.temps : [];
  const doors = latest && Array.isArray(latest.doors) ? latest.doors : [];
  const maxSensors = Math.max(4, temps.length);

  return (
    <div className="manual">
      <section className="card">
        <h2>Monitoreo de cadena de frío</h2>
        <div className="cmd-form">
          <select value={device} onChange={(e) => { setDevice(e.target.value); load(e.target.value); }}>
            <option value="">— Caja / dispositivo —</option>
            {devices.map((d) => (
              <option key={d.uniqueid || d.id} value={d.uniqueid || d.id}>{d.uniqueid || d.name}</option>
            ))}
          </select>
          <button className="mini icon" onClick={() => load()} disabled={loading || !device} title="Refrescar">⟳</button>
        </div>
      </section>

      {!device && (
        <section className="card"><div className="empty">Selecciona una caja para ver sus sensores DS18B20.</div></section>
      )}

      {device && (
        <>
          <section className="stats">
            <div className="card stat"><div className="stat-value">{latest && latest.batt != null ? latest.batt + ' V' : '—'}</div><div className="stat-label">Batería caja</div></div>
            <div className="card stat"><div className="stat-value">{latest && latest.rssi != null ? latest.rssi + ' dBm' : '—'}</div><div className="stat-label">Señal (RSSI)</div></div>
            <div className="card stat"><div className="stat-value">{latest && latest.errors != null ? latest.errors : '—'}</div><div className="stat-label">Errores de lectura</div></div>
            <div className="card stat"><div className="stat-value">{latest ? fmtTime(latest.t) : '—'}</div><div className="stat-label">Última lectura</div></div>
          </section>

          <section className="grid">
            {Array.from({ length: maxSensors }).map((_, i) => {
              const t = temps[i];
              const v = t == null || isNaN(t) ? '—' : Number(t).toFixed(1) + ' °C';
              return (
                <div className="card stat" key={i} style={{ borderLeft: '4px solid ' + tempColor(t) }}>
                  <div className="stat-value" style={{ color: tempColor(t) }}>{v}</div>
                  <div className="stat-label">Sensor {i + 1} (DS18B20)</div>
                </div>
              );
            })}
          </section>

          {doors.length > 0 && (
            <section className="grid">
              {doors.map((d, i) => {
                const open = d === 1 || d === true || d === '1' || d === 'true';
                return (
                  <div className="card stat" key={i} style={{ borderLeft: '4px solid ' + (open ? '#f87171' : '#34d399') }}>
                    <div className="stat-value" style={{ color: open ? '#f87171' : '#34d399' }}>{open ? 'Abierta' : 'Cerrada'}</div>
                    <div className="stat-label">Puerta {i + 1}</div>
                  </div>
                );
              })}
            </section>
          )}

          <section className="card panel">
            <h2>Historial de temperatura</h2>
            {history.length === 0 && <div className="empty">Aún sin lecturas</div>}
            <div style={{ height: 280 }}><canvas ref={chartRef} /></div>
          </section>
        </>
      )}
    </div>
  );
}
